import {gql} from '@apollo/client';
import {groupBy, groupByUniqueKey, compact} from '@benchling/js-utils';
import {DocumentNode} from 'graphql';
import React, {useState, useMemo, useEffect} from 'react';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import WorkspaceItemInaccessibleGuard from 'auth/WorkspaceItemInaccessibleGuard';
import {pluralize, navigate} from 'BenchlingUtil';
import {ActionMethod, ActionOrigin, WORKFLOWS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLArrayToREST} from 'graphql-client/declarative-translations/external/translate';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {assertIsAccessible, isInaccessible, assertAllAccessible} from 'graphql-client/InaccessibleInfo';
import {SchemaById} from 'lookup-config/LookupConfigModalConstants';
import {
  FetchSelectorsByIdQuery,
  FetchSelectorsByIdQueryVariables,
} from 'notebook/structured-tables/hooks/__generated/FetchSelectorsByIdQuery';
import {
  FETCH_SELECTORS_BY_ID_QUERY,
  getSelectorsById,
  SchemaFieldSelectorsDict,
} from 'notebook/structured-tables/hooks/useSelectorsById';
import {StructuredTableKind} from 'notebook/structured-tables/StructuredTableConstants';
import doesEvaluatedTableHaveAnyErrors from 'notebook/tables/errors/doesEvaluatedTableHaveAnyErrors';
import notifications from 'notifications';
import * as ProcessManagementDAO from 'processes/dao/ProcessManagementDAO';
import {RouteToEndNodesParams} from 'processes/dao/ProcessManagementDAO';
import {
  getOutputsFilteredByHumanIds,
  getTasksFilteredByHumanIds,
} from 'processes/dashboard/getModelFilteredByProcessesrUrl';
import {buildTableForAutomatedMappingResults} from 'processes/modals/field-mapping/buildTableForAutomatedMappingResult';
import {outputTableToOutputsForCreate} from 'processes/tables/outputTableToOutputsUtils';
import taskTableToTasks from 'processes/tables/taskTableToTasks';
import {Schema} from 'schemas/SchemaConstants';
import ModalButtonKey from 'test/lib/cypress/shared/ModalButtonKey';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {ExpandableModal} from 'ui-library/modals/ExpandableModal';
import {ModalButton} from 'ui-library/modals/Modal';
import StepNav, {StepItemStatus, StepNavVariant} from 'ui-library/StepNav';
import APIUtil from 'util/APIUtil';
import {withLoadingScreen} from 'util/Promises';
import {assertNever} from 'util/Types';

import {InaccessibleInfoGraphQL} from '../../../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';
import {FixFailedRoutingAndMappingAllSchemasQuery} from './__generated/FixFailedRoutingAndMappingAllSchemasQuery';
import {
  FixFailedRoutingAndMappingModalQuery,
  FixFailedRoutingAndMappingModalQueryVariables,
} from './__generated/FixFailedRoutingAndMappingModalQuery';
import {
  isAsyncDataStartAutomatedMapping,
  isAsyncRecordRoutingRecord,
  FromItemIdsToTables,
  RoutingErrorRecord,
} from './FixFailedRoutingAndMappingConstants';
import PerformManualMappingPane from './PerformManualMappingPane';
import SelectDownstreamNodesPane from './SelectDownstreamNodesPane';
import SelectErrorPane from './SelectErrorPane';

type ProcessTask = ExtractByTypename<FixFailedRoutingAndMappingModalQuery['processTask'], 'ProcessTaskNode'>;

type ProcessOutput = ProcessTask['outputs'][number];

type ProcessAsyncRecord = ProcessTask['processAsyncRecords'][number];

type SchemaMapper = ExtractByTypename<
  ProcessAsyncRecord['asyncData'],
  'ProcessFlowchartExecuteSchemaMapperRecordNode'
>['schemaMapper'];

const SCHEMA_MAPPER_FRAGMENT: DocumentNode = gql`
  fragment FixFailedRoutingAndMappingSchemaMapper on ProcessFlowchartSchemaMapperNode {
    id
    toNodeConfig {
      id
    }
    fromSchemas {
      ... on ProcessTaskSchemaNode {
        id
        name
      }
      ... on ProcessOutputSchemaNode {
        id
        name
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
    toSchema {
      ... on ProcessTaskSchemaNode {
        id
        name
      }
      ... on ProcessOutputSchemaNode {
        id
        name
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
`;

export const FIX_FAILED_ROUTING_AND_MAPPING_MODAL_QUERY = gql`
  query FixFailedRoutingAndMappingModalQuery($processTaskId: StrictString!)
  @skipAudit
  @engComponent(name: WORKFLOWS) {
    processTask(id: $processTaskId) {
      ... on ProcessTaskNode {
        id
        humanId
        rootTask {
          id
          process {
            id
          }
          taskSchema {
            ... on ProcessTaskSchemaNode {
              id
            }
            ... on InaccessibleInfoNode {
              ...InaccessibleInfoGraphQL
            }
          }
        }
        process {
          id
        }
        fields
        outputs {
          id
          humanId
          fields
          ...HasArchiveRecordGraphQL
        }
        taskSchema {
          ... on ProcessTaskSchemaNode {
            id
            name
            fields
            allFields
            clientOrigin
            outputSchema {
              id
              name
              fields
              allFields
            }
            statusLifecycle {
              id
              schemaExecutionType
            }
          }
          ... on InaccessibleInfoNode {
            ...InaccessibleInfoGraphQL
          }
        }
        processAsyncRecords {
          id
          status
          details
          asyncData {
            ... on ProcessStartAutomatedMappingRecordNode {
              id
              blockedEndNodesAndExpressions {
                blockedEndNode {
                  ... on ProcessEndNodeDetailsNode {
                    ...ProcessEndNodeDetailsGraphQL
                  }
                }
                blockingExpression {
                  ... on BenchlingExpressionNode {
                    expressionJson
                  }
                  ... on InaccessibleInfoNode {
                    ...InaccessibleInfoGraphQL
                  }
                }
                fromSchemas {
                  ... on ProcessTaskSchemaNode {
                    id
                    name
                  }
                  ... on ProcessOutputSchemaNode {
                    id
                    name
                  }
                  ... on InaccessibleInfoNode {
                    ...InaccessibleInfoGraphQL
                  }
                }
              }
              blockedMappersAndExpressions {
                schemaMapper {
                  ... on ProcessFlowchartSchemaMapperNode {
                    ...FixFailedRoutingAndMappingSchemaMapper
                  }
                }
                blockingExpressions {
                  ... on BenchlingExpressionNode {
                    expressionJson
                  }
                  ... on InaccessibleInfoNode {
                    ...InaccessibleInfoGraphQL
                  }
                }
                blockingRouters {
                  ... on ProcessRouterNodeDetailsNode {
                    id
                  }
                }
              }
            }
            ... on ProcessFlowchartSchemaMappingRecordNode {
              id
              schemaMapper {
                ... on ProcessFlowchartSchemaMapperNode {
                  ...FixFailedRoutingAndMappingSchemaMapper
                }
              }
            }
            ... on ProcessFlowchartExecuteSchemaMapperRecordNode {
              id
              schemaMapper {
                ... on ProcessFlowchartSchemaMapperNode {
                  ...FixFailedRoutingAndMappingSchemaMapper
                }
              }
            }
          }
        }
        ...StudyableTypedValueDataGraphQL
      }
      ...HasArchiveRecordGraphQL
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('StudyableTypedValueData').fragment}
  ${getFragmentWrapper('HasArchiveRecord').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${getFragmentWrapper('ProcessEndNodeDetails').fragment}
  ${SCHEMA_MAPPER_FRAGMENT}
`;

export const GET_ALL_SCHEMAS_QUERY = gql`
  query FixFailedRoutingAndMappingAllSchemasQuery @skipAudit @engComponent(name: WORKFLOWS) {
    ...AllStorableSchemasGraphQL
    ...AllTagSchemasGraphQL
    ...AllResultSchemasGraphQL
    ...AllProcessTaskSchemasForSchemaConfigGraphQL
  }
  ${getFragmentWrapper('AllStorableSchemas').fragment}
  ${getFragmentWrapper('AllTagSchemas').fragment}
  ${getFragmentWrapper('AllResultSchemas').fragment}
  ${getFragmentWrapper('AllProcessTaskSchemasForSchemaConfig').fragment}
`;

export enum FixFailedRoutingAndMappingStepKey {
  SELECT_ERRORS = 'SELECT_ERRORS',
  SELECT_DOWNSTREAM_NODES = 'SELECT_DOWNSTREAM_NODES',
  TABLE_REVIEW = 'TABLE_REVIEW',
}

const SELECT_ERRORS_PANE = {
  key: FixFailedRoutingAndMappingStepKey.SELECT_ERRORS,
  title: 'Select error(s)',
};
const SELECT_DOWNSTREAM_NODES_PANE = {
  key: FixFailedRoutingAndMappingStepKey.SELECT_DOWNSTREAM_NODES,
  title: 'Select next step(s) in workflow',
};
const PERFORM_MANUAL_MAPPING_PANE = {
  key: FixFailedRoutingAndMappingStepKey.TABLE_REVIEW,
  title: 'Review',
};

const DEFAULT_STATUS_BY_STEP = {
  [FixFailedRoutingAndMappingStepKey.SELECT_ERRORS]: StepItemStatus.UNVISITED,
  [FixFailedRoutingAndMappingStepKey.SELECT_DOWNSTREAM_NODES]: StepItemStatus.UNVISITED,
  [FixFailedRoutingAndMappingStepKey.TABLE_REVIEW]: StepItemStatus.UNVISITED,
};

function getFixFailedRoutingAndMappingModalSteps(
  selectedRecord: ProcessAsyncRecord | null
): Array<{key: FixFailedRoutingAndMappingStepKey; title: string}> {
  if (selectedRecord != null && isAsyncRecordRoutingRecord(selectedRecord)) {
    return [SELECT_ERRORS_PANE, SELECT_DOWNSTREAM_NODES_PANE, PERFORM_MANUAL_MAPPING_PANE];
  }
  return [SELECT_ERRORS_PANE, PERFORM_MANUAL_MAPPING_PANE];
}

interface FixFailedRoutingAndMappingModalWithData {
  isLoading: false;
  selectorsById: SchemaFieldSelectorsDict;
  selectedItem: ProcessTask;
  onRequestClose: () => void;
  allSchemasById: SchemaById;
}

interface FixFailedRoutingAndMappingModalStillLoading {
  isLoading: true;
  selectorsById?: null;
  selectedItem?: null;
  onRequestClose: () => void;
  allSchemasById?: null;
}

type FixFailedRoutingAndMappingModalProps =
  | FixFailedRoutingAndMappingModalWithData
  | FixFailedRoutingAndMappingModalStillLoading;

interface QueryingFixFailedMappingMappingModalProps {
  selectedId: string;
  onRequestClose: () => void;
}

function getItemsToMapFromRoutingError(
  asyncRecord: RoutingErrorRecord,
  task: ProcessTask
): Array<ProcessTask> | Array<ProcessOutput> {
  assert(
    asyncRecord.asyncData.blockedMappersAndExpressions.length > 0,
    'Expected at least one router on the path'
  );
  const schemas = assertAllAccessible(
    asyncRecord.asyncData.blockedMappersAndExpressions.map(({schemaMapper}) => {
      assert(schemaMapper.fromSchemas.length === 1, 'Expected routers to block non-merging mappers');
      return schemaMapper.fromSchemas[0];
    })
  );
  assert(
    schemas.every(({id}) => id === schemas[0].id),
    'Expected all blocked mappers to be from the same schema'
  );
  const schemaId = schemas[0].id;
  if (APIUtil.isProcessOutputSchema(schemaId)) {
    return task.outputs;
  }
  return [task];
}

function getSchemaMappers(asyncRecord: ProcessAsyncRecord): Array<SchemaMapper> {
  return isAsyncDataStartAutomatedMapping(asyncRecord.asyncData)
    ? asyncRecord.asyncData.blockedMappersAndExpressions.map(({schemaMapper}) => schemaMapper)
    : [asyncRecord.asyncData.schemaMapper];
}

// With lazy task group creation, we use schema names instead of node names.
function getMapperIdtoDownstreamSchemaName(
  asyncRecord: ProcessAsyncRecord,
  task: ProcessTask
): {[id: string]: string} {
  const schemaMappers = getSchemaMappers(asyncRecord);

  return schemaMappers.reduce(
    (acc, schemaMapper) => {
      acc[schemaMapper.id] = isInaccessible(schemaMapper.toSchema)
        ? 'Inaccessible schema'
        : schemaMapper.toSchema.name;
      return acc;
    },
    {} as {[id: string]: string}
  );
}

function trackAnalytics(selectedItem: ProcessTask, isRoutingError: boolean): void {
  const rootTask = selectedItem.rootTask;
  assert(!isInaccessible(selectedItem.taskSchema), 'Inaccessible task schema in FFM analytics');
  analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.CORRECTED_FAILED_AUTOMATED_MAPPING, {
    process_client_origin: !isInaccessible(selectedItem.taskSchema)
      ? selectedItem.taskSchema.clientOrigin
      : null,
    output_api_id: null,
    task_api_id: selectedItem.id,
    task_group_api_id: selectedItem.process.id,
    flowchart_task_api_id: rootTask?.id ?? null,
    flowchart_task_group_api_id: rootTask?.process.id ?? null,
    task_schema_type: selectedItem.taskSchema.statusLifecycle.schemaExecutionType,
    is_routing_failure: isRoutingError,
  });
}

export function FixFailedRoutingAndMappingModal(props: FixFailedRoutingAndMappingModalProps): JSX.Element {
  const [currentPane, setCurrentPane] = useState(FixFailedRoutingAndMappingStepKey.SELECT_ERRORS);
  const [selectedRecord, setSelectedRecord] = useState<ProcessAsyncRecord | null>(null);
  const [itemsToMap, setItemsToMap] = useState<Array<ProcessTask> | Array<ProcessOutput> | null>(null);
  const [itemIdsToMapperId, setItemIdsToMapperIds] = useState<{[id: string]: Array<string>}>({});
  const [itemIdsToEndNodeId, setItemIdsToEndNodeIds] = useState<{[id: string]: Array<string>}>({});
  const [fromItemIdsToTables, setFromItemIdsToTables] = useState<FromItemIdsToTables | null>(null);

  const mapperIdsToDownstreamSchemaName = useMemo(
    () =>
      selectedRecord != null && props.selectedItem != null
        ? getMapperIdtoDownstreamSchemaName(selectedRecord, props.selectedItem)
        : null,
    [selectedRecord, props.selectedItem]
  );
  const mapperIdToMapper = useMemo(
    () => (selectedRecord != null ? groupByUniqueKey(getSchemaMappers(selectedRecord), ({id}) => id) : null),
    [selectedRecord]
  );

  useEffect(() => {
    const loadAutomatedMappingResults = async (): Promise<void> => {
      const mappingResult = await ProcessManagementDAO.getAutomatedMappingResult({
        automatedMappingInputs: Object.entries(itemIdsToMapperId)
          .map(([taskOrOutputId, mapperIds]) =>
            mapperIds.map((mapperId) => ({
              mapperId,
              ...(APIUtil.isProcessTask(taskOrOutputId)
                ? {taskId: taskOrOutputId}
                : {outputId: taskOrOutputId}),
            }))
          )
          .flat(1),
      });

      const mappingResultsByFromItemId = groupBy(mappingResult.results, ({fromItemId}) => fromItemId);

      const tempFromItemIdToTables = Object.entries(mappingResultsByFromItemId).reduce(
        (acc, [currFromItem, currMappingResults]) => {
          const resultsByMapper = groupBy(currMappingResults, (result) => result.mapperId);
          acc[currFromItem] = Object.entries(resultsByMapper).map(([mapperId, results]) => {
            const fieldsAndValues = results.map(({fields}) => fields);
            const structuredTable = buildTableForAutomatedMappingResults(
              results[0].toSchema,
              fieldsAndValues,
              props.selectorsById!
            );

            return {
              structuredTable,
              computedTable: new Array(structuredTable.rows.length).fill([]),
              mapper: mapperIdToMapper![mapperId],
            };
          });
          return acc;
        },
        {} as FromItemIdsToTables
      );

      // Since we also want the review pane to display items that don't map to anything, we assign
      // them a default value.
      if (itemsToMap != null) {
        for (const itemToMap of itemsToMap) {
          if (tempFromItemIdToTables[itemToMap.id] == null) {
            tempFromItemIdToTables[itemToMap.id] = [];
          }
        }
      }

      setFromItemIdsToTables(tempFromItemIdToTables);
    };

    if (currentPane === FixFailedRoutingAndMappingStepKey.TABLE_REVIEW) {
      loadAutomatedMappingResults();
    }
  }, [currentPane, props.selectorsById, itemIdsToMapperId, mapperIdToMapper, itemsToMap]);

  const resetFlow = (): void => {
    setSelectedRecord(null);
    setItemsToMap(null);
    setItemIdsToMapperIds({});
    setItemIdsToEndNodeIds({});
    setFromItemIdsToTables(null);
    setCurrentPane(FixFailedRoutingAndMappingStepKey.SELECT_ERRORS);
  };

  const hasBlockedExpressions = (asyncRecord: ProcessAsyncRecord): boolean =>
    isAsyncRecordRoutingRecord(asyncRecord) &&
    asyncRecord.asyncData.blockedMappersAndExpressions.some(
      ({blockingExpressions}) => blockingExpressions.length > 0
    );

  const handleSelectedRecord = (asyncRecord: ProcessAsyncRecord): void => {
    assert(props.selectedItem != null, 'Expected item to be selected');
    let nextPane: FixFailedRoutingAndMappingStepKey;
    const tempItemIdsToMapperIds: {[id: string]: Array<string>} = {};
    const tempItemIdsToEndNodeIds: {[id: string]: Array<string>} = {};
    let tempItemsToMap: Array<ProcessTask> | Array<ProcessOutput>;
    // The user should advance to the select downstream nodes modal if they have selected a "routing error"
    // and if the relevant mappers are actually blocked by routing nodes.
    // It's possible for the `start_automated_mapping` task to fail for tasks that do not need to be routed
    // (for example infrastructure issues which cause the celery task to time out) so we detect those kinds of
    // tasks and treat them like "mapping errors" even if the async error is technically a routing error.
    if (isAsyncRecordRoutingRecord(asyncRecord) && hasBlockedExpressions(asyncRecord)) {
      tempItemsToMap = getItemsToMapFromRoutingError(asyncRecord, props.selectedItem);
      nextPane = FixFailedRoutingAndMappingStepKey.SELECT_DOWNSTREAM_NODES;
      for (const item of tempItemsToMap!) {
        tempItemIdsToMapperIds[item.id] = [];
        tempItemIdsToEndNodeIds[item.id] = [];
      }
    } else {
      const schemaMapperIds = isAsyncDataStartAutomatedMapping(asyncRecord.asyncData)
        ? asyncRecord.asyncData.blockedMappersAndExpressions.map(({schemaMapper}) => schemaMapper.id)
        : [asyncRecord.asyncData.schemaMapper.id];
      nextPane = FixFailedRoutingAndMappingStepKey.TABLE_REVIEW;
      tempItemsToMap = [props.selectedItem];
      tempItemIdsToMapperIds[props.selectedItem.id] = schemaMapperIds;
    }
    setSelectedRecord(asyncRecord);
    setCurrentPane(nextPane);
    setItemsToMap(tempItemsToMap);
    setItemIdsToMapperIds(tempItemIdsToMapperIds);
    setItemIdsToEndNodeIds(tempItemIdsToEndNodeIds);
  };

  const submitButton = {
    key: ModalButtonKey.SUBMIT,
    text: 'Submit',
    dataTestAttrs: {
      component: 'FixFailedRoutingAndMappingModal',
      element: 'submitButton',
      key: ModalButtonKey.SUBMIT,
    },
    style: ButtonStyle.PRIMARY,
    onClick: async () => {
      const taskSpecs: Array<ProcessManagementDAO.CreateServerProcessTask> = [];
      const outputSpecs: Array<ProcessManagementDAO.CreateServerProcessOutput> = [];
      const rootTask = props.selectedItem!.rootTask ?? props.selectedItem!;
      if (fromItemIdsToTables != null) {
        for (const [fromItemId, mappingData] of Object.entries(fromItemIdsToTables)) {
          for (const data of mappingData) {
            if (data.structuredTable.kind === StructuredTableKind.PROCESS_TASK) {
              taskSpecs.push(
                ...taskTableToTasks({
                  table: data.structuredTable,
                  evaluatedTable: data.computedTable,
                  sourceType: 'continue_flowchart_dashboard',
                  actionOrigin: ActionOrigin.WORKFLOWS_DASHBOARD,
                  actionMethod: ActionMethod.BUTTON_CLICK,
                }).map((task) => ({
                  ...task,
                  nodeConfigId: data.mapper.toNodeConfig.id,
                  fromItemId,
                  schemaMapperId: data.mapper.id,
                }))
              );
            } else if (data.structuredTable.kind === StructuredTableKind.PROCESS_OUTPUT) {
              outputSpecs.push(
                ...outputTableToOutputsForCreate({
                  table: data.structuredTable,
                  evaluatedTable: data.computedTable,
                  sourceType: 'continue_flowchart_dashboard',
                  actionOrigin: ActionOrigin.WORKFLOWS_DASHBOARD,
                  actionMethod: ActionMethod.BUTTON_CLICK,
                  schemaMapperId: data.mapper.id,
                  taskIds: new Array(data.structuredTable.rows.length).fill(rootTask.id),
                  fromItemIds: new Array(data.structuredTable.rows.length).fill(fromItemId),
                  creationOriginIds: new Array(data.structuredTable.rows.length).fill(rootTask.process.id),
                })
              );
            }
          }
        }
      }
      const endNodeSpecs = selectedRecord
        ? Object.entries(itemIdsToEndNodeId)
            .map(([_, endNodeIds]) =>
              endNodeIds.length > 0
                ? {
                    endNodeIds,
                    asyncJobRecordId: selectedRecord.id,
                  }
                : null
            )
            .filter((endNodeSpec): endNodeSpec is RouteToEndNodesParams => endNodeSpec != null)
        : [];

      const itemsBeingCreated = [
        ...(taskSpecs.length > 0 ? [pluralize(taskSpecs.length, 'task')] : []),
        ...(outputSpecs.length > 0 ? [pluralize(outputSpecs.length, 'output')] : []),
      ].join(' and ');
      const response = await withLoadingScreen(
        ProcessManagementDAO.fixFailedRoutingAndMapping({
          tasks: taskSpecs,
          outputs: outputSpecs,
          taskId: props.selectedItem!.id,
          endNodes: endNodeSpecs,
        }),
        itemsBeingCreated.length > 0 ? `Creating new ${itemsBeingCreated}` : 'Performing routing'
      );
      if (taskSpecs.length > 0) {
        navigate(null, getTasksFilteredByHumanIds(response.createdTasks.map((task) => task.humanId)));
      } else if (outputSpecs.length > 0) {
        navigate(null, getOutputsFilteredByHumanIds(response.createdOutputs.map((output) => output.humanId)));
      } else {
        navigate(null, getTasksFilteredByHumanIds([response.routedTask.humanId]));
      }
      notifications.success(
        itemsBeingCreated.length > 0 ? `Created new ${itemsBeingCreated}.` : 'Routing complete.'
      );

      trackAnalytics(
        props.selectedItem!,
        Boolean(selectedRecord && isAsyncRecordRoutingRecord(selectedRecord))
      );
      props.onRequestClose();
    },
    disabled:
      fromItemIdsToTables == null ||
      Object.values(fromItemIdsToTables).some((mapperDatas) =>
        mapperDatas.some(({computedTable}) => doesEvaluatedTableHaveAnyErrors(computedTable))
      ),
  };

  const getModalButtons = (): Array<ModalButton> => {
    switch (currentPane) {
      case FixFailedRoutingAndMappingStepKey.SELECT_ERRORS:
        return [];
      case FixFailedRoutingAndMappingStepKey.SELECT_DOWNSTREAM_NODES:
        return [
          {
            key: ModalButtonKey.BACK,
            text: 'Back',
            dataTestAttrs: {
              component: 'FixFailedRoutingAndMappingModal',
              element: 'backButton',
              key: ModalButtonKey.BACK,
            },
            onClick: () => {
              resetFlow();
            },
          },
          {
            key: ModalButtonKey.NEXT,
            text: 'Next',
            dataTestAttrs: {
              component: 'FixFailedRoutingAndMappingModal',
              element: 'nextButton',
              key: ModalButtonKey.NEXT,
            },
            style: ButtonStyle.PRIMARY,
            onClick: () => {
              setCurrentPane(FixFailedRoutingAndMappingStepKey.TABLE_REVIEW);
            },
            disabled: Object.entries(itemIdsToMapperId).some(
              ([itemId, mapperIds]) => mapperIds.length === 0 && itemIdsToEndNodeId[itemId].length === 0
            ),
          },
        ];

      case FixFailedRoutingAndMappingStepKey.TABLE_REVIEW:
        return [
          {
            key: ModalButtonKey.BACK,
            text: 'Back',
            dataTestAttrs: {
              component: 'FixFailedRoutingAndMappingModal',
              element: 'backButton',
              key: ModalButtonKey.BACK,
            },
            onClick: () => {
              if (
                selectedRecord != null &&
                isAsyncDataStartAutomatedMapping(selectedRecord.asyncData) &&
                hasBlockedExpressions(selectedRecord)
              ) {
                setCurrentPane(FixFailedRoutingAndMappingStepKey.SELECT_DOWNSTREAM_NODES);
              } else {
                resetFlow();
              }
            },
          },
          submitButton,
        ];
      default:
        assert(false, `Unexpected type ${assertNever(currentPane)}`);
        return [];
    }
  };

  const getModalBody = (): JSX.Element | null => {
    if (props.isLoading) {
      return <CenteredHorizontalThrobber />;
    }
    switch (currentPane) {
      case FixFailedRoutingAndMappingStepKey.SELECT_ERRORS:
        return (
          <SelectErrorPane
            asyncRecords={props.selectedItem.processAsyncRecords}
            onSelectError={handleSelectedRecord}
          />
        );
      case FixFailedRoutingAndMappingStepKey.SELECT_DOWNSTREAM_NODES:
        assert(
          itemsToMap != null && selectedRecord != null && mapperIdsToDownstreamSchemaName != null,
          'Expected record to be selected and mappable items to be defined'
        );
        assert(isAsyncRecordRoutingRecord(selectedRecord), 'Expected selected record to be a routing error');
        return (
          <SelectDownstreamNodesPane
            itemIdsToSelectedMapperIds={itemIdsToMapperId}
            itemIdsToSelectedEndNodeIds={itemIdsToEndNodeId}
            itemsToMap={itemsToMap}
            mapperIdsToDownstreamSchemaName={mapperIdsToDownstreamSchemaName}
            asyncRecord={selectedRecord}
            selectorsById={props.selectorsById}
            task={props.selectedItem}
            onChangeItemIdsToSelectedMapperIds={setItemIdsToMapperIds}
            onChangeItemIdsToSelectedEndNodeIds={setItemIdsToEndNodeIds}
            allSchemasById={props.allSchemasById}
          />
        );
      case FixFailedRoutingAndMappingStepKey.TABLE_REVIEW: {
        assert(
          mapperIdsToDownstreamSchemaName != null && itemsToMap != null,
          'Expected flowchart data to be defined'
        );
        return (
          <PerformManualMappingPane
            fromItems={itemsToMap}
            mapperIdsToDownstreamSchemaName={mapperIdsToDownstreamSchemaName}
            fromItemIdsToTables={fromItemIdsToTables}
            onChangeFromItemIdsToTables={setFromItemIdsToTables}
          />
        );
      }
      default:
        assert(false, `Unexpected type ${assertNever(currentPane)}`);
        return null;
    }
  };

  return (
    <ExpandableModal
      title="Fix failed mapping errors"
      onRequestClose={props.onRequestClose}
      buttons={getModalButtons()}
      expandHeight={true}
      large={true}
      modalKey="fixFailedMapping"
      rememberExpandedSetting={true}
      wrapInForm={false}
    >
      <StepNav
        steps={getFixFailedRoutingAndMappingModalSteps(selectedRecord)}
        statusByStep={DEFAULT_STATUS_BY_STEP}
        currentStepKey={currentPane}
        onUpdateCurrentStep={() => {}}
        variant={StepNavVariant.FLAT}
      />
      {getModalBody()}
    </ExpandableModal>
  );
}

function getInaccessibleItems(
  queryData: FixFailedRoutingAndMappingModalQuery
): Array<InaccessibleInfoGraphQL> {
  const inaccessibleItems: Array<InaccessibleInfoGraphQL> = [];

  const task = queryData.processTask;
  if (isInaccessible(task)) {
    return [task];
  }

  if (isInaccessible(task.taskSchema)) {
    inaccessibleItems.push(task.taskSchema);
  }

  for (const asyncRecord of task.processAsyncRecords) {
    const schemaMappers = getSchemaMappers(asyncRecord);
    for (const schemaMapper of schemaMappers) {
      if (isInaccessible(schemaMapper.toSchema)) {
        inaccessibleItems.push(schemaMapper.toSchema);
      }
    }
  }
  return inaccessibleItems;
}

export default function QueryingFixFailedRoutingAndMappingModal(
  props: QueryingFixFailedMappingMappingModalProps
): JSX.Element {
  const allSchemasQueryResult =
    useBenchlingQuery<FixFailedRoutingAndMappingAllSchemasQuery>(GET_ALL_SCHEMAS_QUERY);
  const selectorQueryResult = useBenchlingQuery<FetchSelectorsByIdQuery, FetchSelectorsByIdQueryVariables>(
    FETCH_SELECTORS_BY_ID_QUERY,
    {
      variables: {includeArchived: true},
    }
  );
  const queryResult = useBenchlingQuery<
    FixFailedRoutingAndMappingModalQuery,
    FixFailedRoutingAndMappingModalQueryVariables
  >(FIX_FAILED_ROUTING_AND_MAPPING_MODAL_QUERY, {
    variables: {processTaskId: props.selectedId},
  });
  const selectors = graphQLArrayToREST(
    'SchemaFieldSelector',
    selectorQueryResult.type === QueryResultType.SUCCESS ? selectorQueryResult.data.schemaFieldSelectors : []
  );
  const selectorsById = getSelectorsById(selectors);

  const inaccessibleItems =
    queryResult.type !== QueryResultType.SUCCESS ? [] : getInaccessibleItems(queryResult.data);
  if (inaccessibleItems.length > 0) {
    return (
      <WorkspaceItemInaccessibleGuard
        inaccessibleInfo={inaccessibleItems[0]}
        modelType={ModelTypeConstants.PROCESS_TASK}
      />
    );
  }
  const isLoading =
    selectorQueryResult.type !== QueryResultType.SUCCESS ||
    queryResult.type !== QueryResultType.SUCCESS ||
    allSchemasQueryResult.type !== QueryResultType.SUCCESS;

  return isLoading ? (
    <FixFailedRoutingAndMappingModal isLoading={true} onRequestClose={props.onRequestClose} />
  ) : (
    <FixFailedRoutingAndMappingModal
      isLoading={false}
      selectorsById={selectorsById}
      selectedItem={assertIsAccessible(queryResult.data.processTask)}
      onRequestClose={props.onRequestClose}
      allSchemasById={groupByUniqueKey(
        [
          ...Object.values(allSchemasQueryResult.data).flat(),
          ...compact(
            allSchemasQueryResult.data.processTaskSchemasSupportingPlatformFeature.map(
              ({outputSchema}) => outputSchema
            )
          ),
        ] as Array<Schema>,
        ({id}) => id
      )}
    />
  );
}
